import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ImageCroppedEvent, ImageCropperComponent } from 'ngx-image-cropper';
import { AuthService } from 'src/app/services/auth.service';
import {
  CollectionReportHistoryItem,
  CollectionReportsService,
} from 'src/app/services/collection-reports.service';
import { UtilsService } from 'src/app/services/utils.service';
import {
  LeafletMapWrapperComponent,
  LeafletMapWrapperStatus,
} from 'src/app/components/shared/leaflet-map-wrapper/leaflet-map-wrapper.component';
import {
  captureElementAsJpegBlob,
} from '../collection-report/collection-report.utils';

export type CollectionReportDialogData = {
  taskId: string;
  taskName?: string;
  selectionsSummaryText: string;
  toiStart?: string;
  toiEnd?: string;
  captureMap: () => Promise<Blob>;
  aoiTarget?: any;
};

@Component({
  selector: 'app-collection-report-dialog',
  templateUrl: './collection-report-dialog.component.html',
  styleUrls: ['./collection-report-dialog.component.scss'],
})
export class CollectionReportDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly reportLimitMax = 5;
  reportLimitBannerVisible = false;

  readonly remarksMaxLength = 1000;
  readonly titleMaxLength = 100;
  readonly cropResizeToWidth = 1600;

  readonly narrativeForm = this.formBuilder.group({
    title: ['', [Validators.required, Validators.maxLength(100)]],
    narrative: ['', [Validators.required, Validators.maxLength(1000)]],
  });

  loading = false;
  historyLoading = false;
  previewLoading = true;
  errorMessage = '';
  reports: Array<CollectionReportHistoryItem & { createdAtDisplay?: string }> = [];
  deletingReportId = '';
  private currentUser: any = null;
  private previewBlob: Blob | null = null;
  private previewObjectUrl = '';
  previewSafeUrl: SafeUrl | null = null;

  cropperImageFile: File | null = null;
  private croppedBlob: Blob | null = null;
  cropLoadFailed = false;
  cropperLoading = false;
  cropperIsReady = false;

  aoiTilesFailed = false;
  aoiGeometryFailed = false;
  aoiDebugSummary = '';

  @ViewChild(ImageCropperComponent) private readonly cropper?: ImageCropperComponent;

  @ViewChild('aoiMapWrapper') private readonly aoiMapWrapper?: LeafletMapWrapperComponent;

  get remarksLength(): number {
    const value = this.narrativeForm.controls.narrative.value;
    return typeof value === 'string' ? value.length : 0;
  }

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly collectionReportsService: CollectionReportsService,
    private readonly authService: AuthService,
    private readonly dialogRef: MatDialogRef<CollectionReportDialogComponent>,
    private readonly sanitizer: DomSanitizer,
    private readonly utilsService: UtilsService,
    @Inject(MAT_DIALOG_DATA) public readonly data: CollectionReportDialogData,
  ) {}

  ngOnDestroy(): void {
    this.revokePreviewUrl();
  }

  ngAfterViewInit(): void {
    // Map wrapper initializes itself when rendered.
  }

  onAoiStatusChange(status: LeafletMapWrapperStatus): void {
    this.aoiTilesFailed = !!status?.tilesFailed;
    this.aoiGeometryFailed = !!status?.geometryFailed;
    this.aoiDebugSummary = status?.debugSummary || '';
  }

  private async captureAoiMapImage(): Promise<Blob> {
    const el = this.aoiMapWrapper?.getCaptureElement();
    if (!el) {
      throw new Error('AOI map is not available for screenshot.');
    }

    // Give Leaflet a moment to paint tiles/vectors.
    this.aoiMapWrapper?.invalidateSize();
    await new Promise<void>((resolve) => setTimeout(() => resolve(), 150));

    return await captureElementAsJpegBlob(el, { aoiOnlyInClone: true, revealAoiInClone: true });
  }

  private revokePreviewUrl(): void {
    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl);
    }
    this.previewObjectUrl = '';
    this.previewSafeUrl = null;
  }

  private setPreviewBlob(blob: Blob): void {
    this.previewBlob = blob;
    this.revokePreviewUrl();
    this.previewObjectUrl = URL.createObjectURL(blob);
    this.previewSafeUrl = this.sanitizer.bypassSecurityTrustUrl(this.previewObjectUrl);

    // Reset cropper state for the new source image.
    this.cropperImageFile = new File([blob], 'collection_map.jpeg', {
      type: blob.type || 'image/jpeg',
    });
    this.croppedBlob = null;
    this.cropLoadFailed = false;
    this.cropperLoading = true;
    this.cropperIsReady = false;
  }

  onImageCropped(event: ImageCroppedEvent): void {
    if (event?.blob) {
      this.croppedBlob = event.blob;
    }

    // The cropper library creates a new object URL on each crop event.
    // We don't use it (we upload the Blob), so revoke to avoid leaking memory.
    if (event?.objectUrl) {
      URL.revokeObjectURL(event.objectUrl);
    }
  }

  onCropperReady(): void {
    this.cropperLoading = false;
    this.cropperIsReady = true;
  }

  onImageLoaded(): void {
    // Some browsers trigger imageLoaded slightly earlier than cropperReady.
    // Clearing the loading overlay here improves perceived performance.
    this.cropperLoading = false;
  }

  onCropLoadFailed(): void {
    this.cropLoadFailed = true;
    this.cropperLoading = false;
    this.cropperIsReady = false;
  }

  async ngOnInit(): Promise<void> {
    this.previewLoading = true;
    this.errorMessage = '';
    this.currentUser = this.authService.userValue || await this.authService.setAndGetCurrentUser();

    // Let the dialog render first to avoid "jerk" while opening.
    await new Promise<void>((resolve) => setTimeout(() => resolve(), 0));
    if (typeof requestAnimationFrame === 'function') {
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    }

    try {
      const blob = await this.data.captureMap();
      this.setPreviewBlob(blob);
    } catch (e: any) {
      this.errorMessage = e?.message || 'Failed to capture preview image.';
    } finally {
      this.previewLoading = false;
    }

    await this.loadHistory();
  }

  async loadHistory(): Promise<void> {
    this.errorMessage = '';
    this.historyLoading = true;
    try {
      const reports = await this.collectionReportsService.list(this.data.taskId);
      this.reports = reports.map((report) => ({
        ...report,
        createdAtDisplay: this.formatReportDate(report.createdAt),
      }));

      // If the user previously hit the limit, hide the banner once they're below it.
      if (this.reports.length < this.reportLimitMax) {
        this.reportLimitBannerVisible = false;
      }
    } catch (e: any) {
      this.errorMessage = e?.error?.msg || e?.message || 'Failed to load report history.';
      this.reports = [];
    } finally {
      this.historyLoading = false;
    }
  }

  private formatReportDate(createdAt?: string): string {
    if (!createdAt) return '-';
    if (this.currentUser?.country) {
      return this.utilsService.convertUtcToLocal(createdAt, this.currentUser);
    }

    return createdAt;
  }

  async generate(): Promise<void> {
    if (this.narrativeForm.invalid) {
      this.narrativeForm.markAllAsTouched();
      return;
    }

    // Enforce a client-side cap to prevent a backend error and guide the user.
    if (this.reports.length >= this.reportLimitMax) {
      this.reportLimitBannerVisible = true;
      return;
    }

    this.errorMessage = '';
    this.loading = true;

    try {
      const title = this.narrativeForm.value.title || '';
      const narrative = this.narrativeForm.value.narrative || '';
      const generatedAt = this.currentUser?.country
        ? this.utilsService.convertUtcToLocal(new Date().toISOString(), this.currentUser)
        : new Date().toISOString();
      const toiText = this.formatToiText();

      const aoiMapImage = await this.captureAoiMapImage();

      // Use the image currently shown in the dialog (cropped if available).
      let mapBlob: Blob | null = this.croppedBlob || null;

      // If the cropper is ready, actively request a fresh crop on generate.
      if (!mapBlob && !this.cropLoadFailed && this.cropperIsReady && this.cropper) {
        try {
          const cropped = await this.cropper.crop('blob');
          mapBlob = (cropped as any)?.blob || null;
        } catch {
          // Fall back to the existing blobs below.
        }
      }

      mapBlob = mapBlob || this.previewBlob;
      if (!mapBlob) {
        throw new Error('Preview image is not ready yet. Please wait and try again.');
      }

      const reportId = await this.collectionReportsService.generate(this.data.taskId, {
        title,
        remarks: narrative,
        generatedAt,
        toiText,
        mapImage: mapBlob,
        aoiMapImage,
        selectionsSummaryText: this.data.selectionsSummaryText,
      });
      if (reportId) {
        await this.open(reportId);
      }

      await this.loadHistory();
    } catch (e: any) {
      this.errorMessage = e?.error?.msg || e?.message || 'Failed to generate report.';
    } finally {
      this.loading = false;
    }
  }

  private formatToiText(): string {
    const start = this.data.toiStart;
    const end = this.data.toiEnd;
    const formatValue = (value?: string) => {
      if (!value) return '';
      if (this.currentUser?.country) {
        return this.utilsService.convertUtcToLocal(value, this.currentUser);
      }
      return value;
    };

    const formattedStart = formatValue(start);
    const formattedEnd = formatValue(end);

    return `${formattedStart || ''}${formattedEnd ? ` - ${formattedEnd}` : ''}`;
  }

  async open(reportId: string): Promise<void> {
    this.errorMessage = '';
    try {
      const signedUrl = await this.collectionReportsService.getSignedUrlWithDisposition(
        this.data.taskId,
        reportId,
        'inline',
      );
      if (signedUrl) {
        window.open(signedUrl, '_blank');
      }
    } catch (e: any) {
      this.errorMessage = e?.error?.msg || e?.message || 'Failed to open report.';
    }
  }

  async download(reportId: string): Promise<void> {
    this.errorMessage = '';
    try {
      const signedUrl = await this.collectionReportsService.getSignedUrlWithDisposition(
        this.data.taskId,
        reportId,
        'attachment',
      );
      if (!signedUrl) return;

      const anchor = document.createElement('a');
      anchor.href = signedUrl;
      anchor.target = '_blank';
      anchor.rel = 'noopener';
      anchor.click();
    } catch (e: any) {
      this.errorMessage = e?.error?.msg || e?.message || 'Failed to download report.';
    }
  }

  async delete(reportId: string): Promise<void> {
    this.errorMessage = '';
    this.deletingReportId = reportId;
    try {
      await this.collectionReportsService.delete(this.data.taskId, reportId);
      await this.loadHistory();
    } catch (e: any) {
      this.errorMessage = e?.error?.msg || e?.message || 'Failed to delete report.';
    } finally {
      this.deletingReportId = '';
    }
  }

  close(): void {
    this.dialogRef.close();
  }
}
